/**
 * src/i18n/context/I18nContext.tsx
 *
 * Core interfaces and Context definition for reactive multilingual translations.
 */

import { createContext, useContext } from 'react';
import { LanguagePack } from '../language-pack-validator';

export interface LanguageMeta {
  code: string;
  name: string;
  direction: 'ltr' | 'rtl';
  version: string;
  isActive: boolean;
}

export interface I18nContextType {
  currentLanguage: string;
  isRTL: boolean;
  installedLanguages: LanguageMeta[];
  t: (key: string, params?: Record<string, string | number | boolean | null | undefined>) => string;
  setLanguage: (code: string) => Promise<void>;
  toggleLanguageActive: (code: string, isActive: boolean) => void;
  importLanguagePack: (rawJson: string | object) => { success: boolean; errors?: string[]; warnings?: string[] };
  exportLanguagePack: (code: string) => LanguagePack | null;
  getPack: (code: string) => LanguagePack | undefined;
}

export const I18nContext = createContext<I18nContextType | null>(null);

export function useTranslation(): I18nContextType {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return ctx;
}
